import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {upsertServiceInList} from '#app/mutations/services/servicesMutations';

export interface Service {
    id: number;
    name: string;
    type_id: number;
    calc_type_id: number;
    period_type_id: number;
    amount: number;
    responsible_id?: number;
    is_archived: boolean;
    [key: string]: any;
}

export interface ServicesState {
    itemsByArchive: Record<0 | 1, Service[]>;
    loadedByArchive: Record<0 | 1, boolean>;
    loading: boolean;
    error: string | null;
    archiveDisplayed: 0 | 1;
}

const initialState: ServicesState = {
    itemsByArchive: {
        0: [],
        1: [],
    },
    loadedByArchive: {
        0: false,
        1: false,
    },
    loading: false,
    error: null,
    archiveDisplayed: 0,
};

export const servicesSlice = createSlice({
    name: 'servicesConstructor',
    initialState,
    reducers: {
        setServices: (state, action: PayloadAction<{ archive: 0 | 1; items: Service[] }>) => {
            const {archive, items} = action.payload;
            state.itemsByArchive[archive] = items;
            state.loadedByArchive[archive] = true;
        },
        upsertService: (state, action: PayloadAction<Service>) => {
            const service = action.payload;
            const targetArchive: 0 | 1 = service.is_archived ? 1 : 0;
            const otherArchive: 0 | 1 = targetArchive === 1 ? 0 : 1;

            state.itemsByArchive[targetArchive] = upsertServiceInList(
                state.itemsByArchive[targetArchive],
                service
            );

            state.itemsByArchive[otherArchive] = state.itemsByArchive[otherArchive].filter(
                (s) => s.id !== service.id
            );

            state.loadedByArchive[targetArchive] = true;
            if (state.itemsByArchive[otherArchive].length > 0) {
                state.loadedByArchive[otherArchive] = true;
            }
        },
        setServicesLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setServicesError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        toggleArchiveDisplayed: (state) => {
            state.archiveDisplayed = state.archiveDisplayed === 1 ? 0 : 1;
        },
        setArchiveDisplayed: (state, action: PayloadAction<0 | 1>) => {
            state.archiveDisplayed = action.payload;
        },
    },
});

export const {
    setServices,
    upsertService,
    setServicesLoading,
    setServicesError,
    toggleArchiveDisplayed,
    setArchiveDisplayed,
} = servicesSlice.actions;

const getDefaultState = (): ServicesState => ({
    itemsByArchive: {
        0: [],
        1: [],
    },
    loadedByArchive: {
        0: false,
        1: false,
    },
    loading: false,
    error: null,
    archiveDisplayed: 0,
});

export const selectServicesState = (state: any): ServicesState =>
    state.servicesConstructor || getDefaultState();

export const selectServices = (state: any): Service[] =>
    selectServicesState(state).itemsByArchive[selectServicesState(state).archiveDisplayed];

export const selectServicesLoading = (state: any): boolean =>
    selectServicesState(state).loading;

export const selectServicesArchiveDisplayed = (state: any): 0 | 1 =>
    selectServicesState(state).archiveDisplayed;

export const selectServicesLoadedForCurrentArchive = (state: any): boolean => {
    const slice = selectServicesState(state);
    return slice.loadedByArchive[slice.archiveDisplayed];
};

export default servicesSlice.reducer;


